import * as React from 'react';

import { RouteStore } from 'bernie-core';

import { AlternateDatesModel, HotelViewModel } from 'models';

import { UitkCardAloha, UitkCardAlohaContentSection } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkSpacing } from 'uitk-react-spacing';

import { AdditionalInfo } from './additional-info';
import { AlternateDates } from './alternate-dates';
import { BadgeGenerator } from 'components/common/badge-generation';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { ListingPlaceholderSmall } from 'src/common/components/infosite/rooms-and-rates/loading-placeholder';
import { ListingPropertyBadges } from './listing-property-badges';
import { Neighborhood } from './neighborhood';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { PrimaryBadge } from './primary-badge';
import { Stars } from './star-rating';
import { TitleHeading } from './title-heading';

interface DefaultListingProps {
  className: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  routeStore?: RouteStore;
  isDatelessPdp?: boolean;
  defaultListingTopMarginClass?: string;
  stars?: number;
  alternateDatesProps?: {
    handleClick: () => void;
    model: AlternateDatesModel[];
  };
  // eslint-disable-next-line no-unused-vars
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  alternateDatesInfo: {
    collapsedLinkName: string;
    collapsedMsgKey: string;
    collapsedRfrr: string;
    expandedLinkName: string;
    expandedMsgKey: string;
    expandedRfrr: string;
    headerTextKey: string;
  };
  imageAspectRatio?: string;
  dataStid?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  displaySecondaryBadge?: boolean;
}

export const DefaultListing: React.FunctionComponent<DefaultListingProps> = ({
  alternateDatesProps,
  index,
  loading,
  model,
  routeStore,
  isDatelessPdp,
  className,
  defaultListingTopMarginClass,
  stars,
  getTitleComponent,
  alternateDatesInfo,
  dataStid,
  cardLinkProps,
  routeStoreGoBack,
  listingClickHandler,
  setItemRef,
  imageAspectRatio,
  displaySecondaryBadge,
}) => {
  const {
    alternateDates,
    listingFooter,
    name,
    neighborhood,
    price,
    propertySummary,
    secondaryCardMessage,
    supportingMessages,
    thumbnail,
    recentlyViewed,
    propertyDetailsLink,
    pinnedDetails,
    propertyListingBadgeModel,
    travelAd,
  } = model;

  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1,
  };

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary,
  };

  const priceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price },
    infositeUrl: propertyDetailsLink?.uri?.relativePath,
    wrapPrice: true,
    viewSmall: true,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    isDatelessPdp,
    index,
  };
  const pinned = Boolean(pinnedDetails?.heading);

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <ListingPlaceholderSmall />
      </li>
    );
  }

  const { secondary } = { ...propertyListingBadgeModel };
  const secondaryIsSafe = secondary && (secondary.text || secondary.icon || secondary.mark);

  return (
    <li {...listingWrapperProps}>
      <UitkCardAloha>
        <ListingName visuallyHidden={true} />
        <div className="has-action listing__card">
          <PrimaryBadge propertyListingBadgeModel={propertyListingBadgeModel} travelAd={travelAd} pinned={pinned} />
          {pinned && <TitleHeading data-stid="title-heading" headingInfo={pinnedDetails} />}
          <ListingErrorBoundary thumbnail={thumbnail} name={name}>
            {getTitleComponent(imageAspectRatio)}
          </ListingErrorBoundary>
          <UitkLayoutPosition type="relative" cloneElement={true}>
            <UitkSpacing padding={'three'}>
              <UitkCardAlohaContentSection className={`listing-content ${defaultListingTopMarginClass}`}>
                {displaySecondaryBadge && secondaryIsSafe && (
                  <UitkLayoutPosition aria-hidden={true} type="absolute" position={{ top: 'minus-four', right: 'two', zindex: 'layer3' }}>
                    <BadgeGenerator badge={secondary} dataStid="secondary-badge" />
                  </UitkLayoutPosition>
                )}
                <UitkLayoutFlex justifyContent="space-between" className="uitk-flex-grow-1">
                  <UitkLayoutFlexItem>
                    <UitkLayoutFlex direction="column" justifyContent="space-between" className="uitk-type-300">
                      <UitkLayoutFlexItem>
                        <div>
                          <ListingPropertyBadges />
                          <ListingName lineCount={2} />
                          <Stars stars={stars} />
                          <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                          <Neighborhood neighborhood={neighborhood} />
                        </div>
                      </UitkLayoutFlexItem>
                      <UitkLayoutFlexItem>
                        <div>
                          <ListingCommonSection model={model} routeStore={routeStore} routeStoreGoBack={routeStoreGoBack} />
                        </div>
                      </UitkLayoutFlexItem>
                    </UitkLayoutFlex>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem minWidth="third_width" grow={1} shrink={0}>
                    <div>
                      {displaySecondaryBadge && secondaryIsSafe && <div className="is-visually-hidden">{secondary.text}</div>}
                      <PriceAndSupportingMessagesContainer {...priceAndSupportingMessagesContainerProps} />
                    </div>
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
                <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
              </UitkCardAlohaContentSection>
            </UitkSpacing>
          </UitkLayoutPosition>
          <ListingFooter {...cardLinkProps} listingFooter={listingFooter} listingClickHandler={listingClickHandler} />
          {alternateDates && (
            <AdditionalInfo {...alternateDatesInfo} displayHeaderText={false} keepOpen={false}>
              <AlternateDates {...alternateDatesProps} />
            </AdditionalInfo>
          )}
        </div>
      </UitkCardAloha>
    </li>
  );
};
